import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Card,
  CardContent,
  Container,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import LinkIcon from '@mui/icons-material/Link';
import Collapse from '@mui/material/Collapse';
import { IPublicClientApplication, AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';

// Types
interface TokenScopeRequesterProps {
  onBack: () => void;
  tokenService: {
    getToken: (scopes: string[], account: AccountInfo, options?: { forceRefresh?: boolean }) => Promise<string>;
  };
  account: AccountInfo;
  msalInstance: IPublicClientApplication;
}

type Preset = {
  id: string;
  name: string;
  scopes: string[];
  resource?: string;
  useDefault?: boolean;
  authority?: string;
  clientIdOverride?: string;
};

// Helpers
function decodeJwt(token: string): { header: any; payload: any } | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const decodePart = (p: string) => JSON.parse(atob(p.replace(/-/g, '+').replace(/_/g, '/')));
    const header = decodePart(parts[0]);
    const payload = decodePart(parts[1]);
    return { header, payload };
  } catch {
    return null;
  }
}

const PRESETS_KEY = 'tokenScopeRequester.presets.v1';

function TokenScopeRequester({ onBack, tokenService, account, msalInstance }: TokenScopeRequesterProps) {
  // State
  const [scopeInput, setScopeInput] = useState('openid profile');
  const [scopes, setScopes] = useState<string[]>(['openid', 'profile']);
  const [resource, setResource] = useState<string>('');
  const [useDefault, setUseDefault] = useState<boolean>(false);
  const [authority, setAuthority] = useState<string>('https://login.microsoftonline.com');
  const [clientIdOverride, setClientIdOverride] = useState<string>('');
  const [presets, setPresets] = useState<Preset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [forceConsent, setForceConsent] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' } | null>(null);
  const [authorityError, setAuthorityError] = useState<string>('');
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  // Test request state
  const [testUrl, setTestUrl] = useState<string>('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ status?: number; ms?: number; wwwAuth?: string; error?: string } | null>(null);
  const [testHint, setTestHint] = useState<string>('');
  const [selectedAccount, setSelectedAccount] = useState<AccountInfo | null>(account || null);
  const [useLoginHint, setUseLoginHint] = useState<boolean>(true);
  const [lastAuthUrl, setLastAuthUrl] = useState<string>('');
  const [authRequestExpanded, setAuthRequestExpanded] = useState(false);

  // Consent status helper state
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [clientSpId, setClientSpId] = useState<string>('');
  const [resourceSpId, setResourceSpId] = useState<string>('');
  const [grants, setGrants] = useState<Array<{ id: string; scope?: string; consentType?: string; principalId?: string; resourceId?: string; clientId?: string }>>([]);
  const grantsGrouped = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const g of grants) {
      const scopeStr = (g.scope || '').trim();
      if (!scopeStr) continue;
      if (!map.has(scopeStr)) map.set(scopeStr, new Set<string>());
      if (g.consentType) map.get(scopeStr)!.add(g.consentType);
    }
    return Array.from(map.entries()).map(([scope, types]) => ({ scope, consentTypes: Array.from(types) }));
  }, [grants]);
  // App-role assignments state
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [roleAssignments, setRoleAssignments] = useState<Array<{ id: string; appRoleId: string; appRoleValue?: string; appRoleDisplayName?: string }>>([]);

  // Tenant options for authority selection (similar to RawOAuthTester)
  const tenantOptions = useMemo(() => {
    const list = [
      { value: 'common', label: 'common (Multi-tenant + Personal)' },
      { value: 'organizations', label: 'organizations (Multi-tenant)' },
    ];
    if (account?.tenantId) {
      list.push({ value: account.tenantId, label: `${account.tenantId} (Current tenant)` });
    }
    return list;
  }, [account?.tenantId]);

  const selectedTenant = useMemo(() => {
    try {
      const a = authority.trim();
      const m = a.match(/^https:\/\/login\.microsoftonline\.com\/(.+?)(?:[\/?#].*)?$/i);
      return m ? m[1] : '';
    } catch {
      return '';
    }
  }, [authority]);

  const graphGetPresets = useMemo(
    () => [
      { label: 'Me profile', url: 'https://graph.microsoft.com/v1.0/me', hint: 'User.Read' },
      { label: 'My memberships', url: 'https://graph.microsoft.com/v1.0/me/memberOf', hint: 'User.Read' },
      { label: 'List users (top 10)', url: 'https://graph.microsoft.com/v1.0/users?$top=10', hint: 'User.ReadBasic.All or User.Read.All' },
      { label: 'List groups (top 10)', url: 'https://graph.microsoft.com/v1.0/groups?$top=10', hint: 'Group.Read.All' },
      { label: 'List applications (top 10)', url: 'https://graph.microsoft.com/v1.0/applications?$top=10', hint: 'Application.Read.All' },
      { label: 'List service principals (top 10)', url: 'https://graph.microsoft.com/v1.0/servicePrincipals?$top=10', hint: 'Application.Read.All' },
      { label: 'My drive root children', url: 'https://graph.microsoft.com/v1.0/me/drive/root/children', hint: 'Files.Read' },
    ],
    []
  );
  const openSnack = (message: string, severity: 'success' | 'info' | 'error' = 'success') => setSnack({ open: true, message, severity });
  const closeSnack = (_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnack((s) => (s ? { ...s, open: false } : s));
  };
  const decoded = useMemo(() => (token ? decodeJwt(token) : null), [token]);

  // Parse the last authorization URL for display (similar to RawOAuthTester)
  const parsedAuth = useMemo(() => {
    if (!lastAuthUrl) return null;
    try {
      const u = new URL(lastAuthUrl);
      const endpoint = `${u.origin}${u.pathname}`;
      const rawEntries = Array.from(u.searchParams.entries());
      const order = [
        'client_id',
        'redirect_uri',
        'scope',
        'response_type',
        'response_mode',
        'state',
        'nonce',
        'prompt',
        'code_challenge',
        'code_challenge_method',
        'login_hint',
        'domain_hint',
      ];
      const inOrder: Array<[string, string]> = [];
      const remaining: Array<[string, string]> = [];
      for (const e of rawEntries) {
        (order.includes(e[0]) ? inOrder : remaining).push(e);
      }
      const sortedKnown = order.flatMap((k) => inOrder.filter(([name]) => name === k));
      const sortedRemaining = remaining.sort((a, b) => a[0].localeCompare(b[0]));
      const params = [...sortedKnown, ...sortedRemaining];
      return { endpoint, params };
    } catch {
      return null;
    }
  }, [lastAuthUrl]);

  const activeAccount = selectedAccount || account;

  // Load presets
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PRESETS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Validate structure
        if (Array.isArray(parsed)) {
          setPresets(parsed.filter(p => p && p.id && p.name));
        }
      }
    } catch (err) {
      console.warn('Failed to load presets:', err);
    }
  }, []);

  const persistPresets = (next: Preset[]) => {
    setPresets(next);
    try {
      localStorage.setItem(PRESETS_KEY, JSON.stringify(next));
    } catch {}
  };

  // Preset actions
  const addScope = () => {
    const s = scopeInput.trim();
    if (!s) return;
    const incoming = s.split(/[ ,\n]+/).filter(Boolean);
    const before = new Set(scopes);
    let added = 0;
    const merged = [...scopes];
    for (const it of incoming) {
      if (!before.has(it)) {
        before.add(it);
        merged.push(it);
        added++;
      }
    }
    const skipped = incoming.length - added;
    setScopes(merged);
    setScopeInput('');
    if (added || skipped) {
      openSnack(
        added && skipped
          ? `Added ${added} scope(s); skipped ${skipped} duplicate(s)`
          : added
          ? `Added ${added} scope(s)`
          : `Skipped ${skipped} duplicate(s)`,
        'info'
      );
    }
  };
  const removeScope = (s: string) => setScopes(scopes.filter((x) => x !== s));
  const savePreset = () => {
    const name = prompt('Preset name?');
    if (!name) return;
    const preset: Preset = { id: `${Date.now()}`, name, scopes, resource, useDefault, authority, clientIdOverride };
    const next = [...presets, preset];
    persistPresets(next);
    setSelectedPresetId(preset.id);
    openSnack('Preset saved', 'success');
  };
  const updatePreset = () => {
    if (!selectedPresetId) {
      openSnack('No preset selected', 'error');
      return;
    }
    setUpdateDialogOpen(true);
  };
  const handleUpdatePresetConfirmed = () => {
    const idx = presets.findIndex((p) => p.id === selectedPresetId);
    if (idx < 0) {
      openSnack('Preset not found', 'error');
      setUpdateDialogOpen(false);
      return;
    }
    const updated: Preset = {
      ...presets[idx],
      scopes,
      resource,
      useDefault,
      authority,
      clientIdOverride,
    };
    const next = [...presets];
    next[idx] = updated;
    persistPresets(next);
    openSnack('Preset updated', 'success');
    setUpdateDialogOpen(false);
  };
  const deletePreset = (id: string) => {
    const next = presets.filter((p) => p.id !== id);
    persistPresets(next);
    if (selectedPresetId === id) setSelectedPresetId(null);
    openSnack('Preset deleted', 'info');
  };
  const applyPreset = (id: string) => {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setSelectedPresetId(id);
    setScopes(p.scopes);
    setResource(p.resource || '');
    setUseDefault(!!p.useDefault);
    setAuthority(p.authority || 'https://login.microsoftonline.com');
    setClientIdOverride(p.clientIdOverride || '');
  };

  // Scope computation
  const trimmedResourceMemo = useMemo(() => resource.trim().replace(/\/$/, ''), [resource]);
  const computedScopes = useMemo(() => {
    const trimmedResource = trimmedResourceMemo;
    if (useDefault) {
      // return trimmedResource ? [`${trimmedResource}/.default`] : [];
      const base = trimmedResource || 'https://graph.microsoft.com';
      return [`${base}/.default`];      
    }
    const inputScopes = scopes.filter(Boolean);
    return inputScopes
      .map((s) => {
        const ss = s.trim();
        if (!ss) return '';
        if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(ss) || ss.includes('/')) return ss; // already qualified
        if (trimmedResource) return `${trimmedResource}/${ss}`;
        return ss; // shorthand (Graph-style)
      })
      .filter(Boolean) as string[];
  }, [scopes, useDefault, trimmedResourceMemo]);

  const isGraphResourceMemo = useMemo(() => {
    const trimmedResource = trimmedResourceMemo;
    return !trimmedResource || /graph\.microsoft\.com|00000003-0000-0000-c000-000000000000/i.test(trimmedResource);
  }, [trimmedResourceMemo]);

  const multiResourceWarning = useMemo(() => {
    const keys = new Set<string>();
    for (const sc of computedScopes) {
      const idx = sc.lastIndexOf('/');
      if (idx > 0) keys.add(sc.slice(0, idx));
    }
    return keys.size > 1 ? `Scopes must belong to a single resource. Found: ${Array.from(keys).join(', ')}` : '';
  }, [computedScopes]);

  // Build authorization URL for display (similar to what MSAL constructs)
  const buildAuthorizationUrl = (params: {
    authority: string;
    clientId: string;
    redirectUri: string;
    scopes: string[];
    prompt?: string;
    loginHint?: string;
  }) => {
    const { authority, clientId, redirectUri, scopes, prompt, loginHint } = params;
    const authEndpoint = `${authority}/oauth2/v2.0/authorize`;
    
    const queryParams = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      redirect_uri: redirectUri,
      response_mode: 'query',
      scope: scopes.join(' '),
      state: 'STATE_PLACEHOLDER', // MSAL generates this dynamically
      nonce: 'NONCE_PLACEHOLDER', // MSAL generates this dynamically
      code_challenge_method: 'S256',
      code_challenge: 'PKCE_CHALLENGE_PLACEHOLDER', // MSAL generates actual PKCE
    });
    
    if (prompt) {
      queryParams.set('prompt', prompt);
    }
    if (loginHint) {
      queryParams.set('login_hint', loginHint);
    }
    
    return `${authEndpoint}?${queryParams.toString()}`;
  };

  // Request token
  const handleRequest = async () => {
    setRequesting(true);
    setError(null);
    setToken(null);
    setLastAuthUrl('');
    try {
      const trimmedResource = trimmedResourceMemo;

      if (multiResourceWarning) throw new Error(multiResourceWarning);

      const uniqueScopes = Array.from(new Set(computedScopes));
      if (uniqueScopes.length === 0) throw new Error('No valid scopes computed.');

      const isGraphResource = !trimmedResource || /graph\.microsoft\.com|00000003-0000-0000-c000-000000000000/i.test(trimmedResource);

      const authorityInput = authority.trim();
      let authorityResolved = '';
      if (authorityInput) {
        if (/^https:\/\/login\.microsoftonline\.com\/?$/.test(authorityInput)) {
          authorityResolved = activeAccount?.tenantId ? `${authorityInput.replace(/\/$/, '')}/${activeAccount.tenantId}` : authorityInput;
        } else {
          authorityResolved = authorityInput;
        }
      } else if (!isGraphResource && activeAccount?.tenantId) {
        authorityResolved = `https://login.microsoftonline.com/${activeAccount.tenantId}`;
      }

      const useMsalDirect = !!authorityResolved || forceConsent || forceRefresh || !!clientIdOverride.trim();
      let accessToken: string;

      const effectiveClientId = clientIdOverride.trim() || msalInstance.getConfiguration().auth.clientId;
      const effectiveAuthority = authorityResolved || `https://login.microsoftonline.com/${activeAccount.tenantId || 'common'}`;
      const effectiveRedirectUri = window.location.origin;
      
      const authUrl = buildAuthorizationUrl({
        authority: effectiveAuthority,
        clientId: effectiveClientId,
        redirectUri: effectiveRedirectUri,
        scopes: uniqueScopes,
        prompt: forceConsent ? 'consent' : undefined,
        loginHint: useLoginHint ? activeAccount?.username : undefined,
      });
      
      setLastAuthUrl(authUrl);

      const primaryAccount = account;
      const isDifferentAccount = activeAccount?.homeAccountId !== primaryAccount?.homeAccountId;
      
      // CRITICAL FIX: Always force refresh for different accounts AND skip silent acquisition
      const shouldForceRefresh = forceRefresh || isDifferentAccount;
      const shouldSkipSilent = isDifferentAccount; // New flag

      console.log('Token request:', {
        activeAccount: activeAccount?.username,
        primaryAccount: primaryAccount?.username,
        isDifferentAccount,
        shouldForceRefresh,
        shouldSkipSilent
      });

      if (useMsalDirect) {
        const overrideClientId = clientIdOverride.trim();
        let pca: IPublicClientApplication = msalInstance;
        if (overrideClientId) {
          const created = new PublicClientApplication({
            auth: { clientId: overrideClientId, authority: authorityResolved || undefined, redirectUri: window.location.origin },
            cache: {
              // CRITICAL: Use localStorage to isolate test app from main app's sessionStorage
              // This prevents test app logout from clearing main app tokens
              cacheLocation: "localStorage",
              storeAuthStateInCookie: false
            }
          });
          if (typeof (created as any).initialize === 'function') {
            await (created as any).initialize();
          }
          pca = created;
        }

        const trySilentThenPopup = async () => {
          // CRITICAL: Skip silent acquisition entirely for different accounts
          if (shouldSkipSilent) {
            console.log('Skipping silent acquisition for different account, going straight to popup');
            const res = await pca.acquireTokenPopup({ 
              scopes: uniqueScopes, 
              authority: authorityResolved || undefined, 
              loginHint: useLoginHint ? activeAccount?.username : undefined 
            });
            console.log('Popup acquisition successful for:', res.account?.username);
            return res.accessToken;
          }

          try {
            const accts = overrideClientId ? pca.getAllAccounts() : ([activeAccount].filter(Boolean) as AccountInfo[]);
            const acct = accts[0];
            
            if (!acct) {
              throw new Error('No account available for token acquisition');
            }
            
            console.log('Attempting silent token acquisition for:', acct.username);
            
            const res = await pca.acquireTokenSilent({ 
              scopes: uniqueScopes, 
              account: acct, 
              forceRefresh: shouldForceRefresh,
              authority: authorityResolved || undefined 
            });
            
            console.log('Silent acquisition successful for:', res.account?.username);
            return res.accessToken;
          } catch (err) {
            console.log('Silent acquisition failed, trying popup:', err);
            const res = await pca.acquireTokenPopup({ 
              scopes: uniqueScopes, 
              authority: authorityResolved || undefined, 
              loginHint: useLoginHint ? activeAccount?.username : undefined 
            });
            
            console.log('Popup acquisition successful for:', res.account?.username);
            return res.accessToken;
          }
        };

        if (forceConsent) {
          const res = await pca.acquireTokenPopup({ 
            scopes: uniqueScopes, 
            authority: authorityResolved || undefined, 
            prompt: 'consent', 
            loginHint: useLoginHint ? activeAccount?.username : undefined 
          });
          accessToken = res.accessToken;
        } else {
          accessToken = await trySilentThenPopup();
        }
      } else {
        // For tokenService path, always use popup for different accounts
        if (shouldSkipSilent) {
          console.log('Using popup for tokenService with different account');
          const res = await msalInstance.acquireTokenPopup({
            scopes: uniqueScopes,
            account: activeAccount,
            loginHint: useLoginHint ? activeAccount?.username : undefined
          });
          accessToken = res.accessToken;
        } else {
          accessToken = await tokenService.getToken(uniqueScopes, activeAccount, { 
            forceRefresh: shouldForceRefresh
          });
        }
      }

      setToken(accessToken);
    } catch (e: any) {
      const message = e?.errorMessage || e?.message || String(e);
      setError(message);
    } finally {
      setRequesting(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      openSnack('Copied to clipboard', 'success');
    } catch {
      openSnack('Copy failed', 'error');
    }
  };

  const claim = (k: string) => decoded?.payload?.[k];

  // Switch user via loginPopup
  const switchUser = async () => {
    try {
      const res = await msalInstance.loginPopup({ scopes: ['openid', 'profile'], prompt: 'select_account' });
      if (res.account) {
        setSelectedAccount(res.account);
        // Clear previous results to avoid stale token display for prior user
        setToken(null);
        setError(null);
        setTestUrl('');
        setTestResult(null);
        openSnack(`Signed in as ${res.account.username}`, 'success');
      }
    } catch (e: any) {
      const msg = e?.message || String(e);
      openSnack(msg.includes('user_cancelled') ? 'Switch user canceled' : 'Switch user failed', 'info');
    }
  };

  // NEW: Sign out test user and revert to primary account
  const signOutTestUser = async () => {
    if (!selectedAccount || selectedAccount.username === account?.username) {
      openSnack('No test user to sign out', 'info');
      return;
    }

    try {
      const testAccountToRemove = msalInstance.getAllAccounts().find(
        acc => acc.homeAccountId === selectedAccount.homeAccountId
      );
      
      if (testAccountToRemove) {
        // Add a small delay to ensure MSAL completes logout operations
        await msalInstance.logoutRedirect({
          account: testAccountToRemove,
          onRedirectNavigate: () => false
        });
        
        // Wait a bit for MSAL to clean up
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.warn('Error during test user logout:', error);
    }

    // Reset to the primary AppConfig account
    setSelectedAccount(account);
    
    // Clear all test state
    setToken(null);
    setError(null);
    setTestUrl('');
    setTestResult(null);
    setTestHint('');
    setClientSpId('');
    setResourceSpId('');
    setGrants([]);
    setRoleAssignments([]);
    
    openSnack(`Signed out test user and reverted to primary account: ${account?.username}`, 'success');
  };

  // Consent status helper using Microsoft Graph
  const GRAPH = 'https://graph.microsoft.com';
  const GRAPH_APPID = '00000003-0000-0000-c000-000000000000';

  const getGraphToken = async (): Promise<string> => {
    // Use RW scope (already consented in many tenants) and keep Directory.Read.All for SP/grants reads
    const scopes = [`${GRAPH}/Application.ReadWrite.All`, `${GRAPH}/Directory.Read.All`];
    try {
      const resSilent = await msalInstance.acquireTokenSilent({ scopes, account: activeAccount || undefined });
      return resSilent.accessToken;
    } catch {
      // Do not force consent; let AAD decide if interaction is needed
      const res = await msalInstance.acquireTokenPopup({ scopes, loginHint: useLoginHint ? activeAccount?.username : undefined });
      return res.accessToken;
    }
  };

  const resolveServicePrincipalId = async (accessToken: string, identifier: string): Promise<string> => {
    // identifier can be GUID appId or Application ID URI
    const headers = { Authorization: `Bearer ${accessToken}` } as any;
    const isGuid = /^[0-9a-fA-F-]{36}$/.test(identifier);
    let url = '';
    if (isGuid) {
      url = `${GRAPH}/v1.0/servicePrincipals?$filter=appId eq '${identifier}'`;
    } else {
      const enc = encodeURIComponent(identifier);
      url = `${GRAPH}/v1.0/servicePrincipals?$filter=servicePrincipalNames/any(x:x eq '${identifier}') or identifierUris/any(x:x eq '${identifier}')`;
    }
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`Graph query failed (${res.status})`);
    const data = await res.json();
    const sp = data?.value?.[0];
    if (!sp?.id) throw new Error('Service principal not found');
    return sp.id as string;
  };

  const fetchConsentStatus = async () => {
    setConsentLoading(true);
    setConsentError(null);
    setGrants([]);
    setClientSpId('');
    setResourceSpId('');
    try {
      const tokenG = await getGraphToken();

      // Determine client appId
      const clientAppId = (decoded?.payload?.appid || decoded?.payload?.azp || clientIdOverride || (msalInstance as any)?.config?.auth?.clientId || '').toString();
      if (!clientAppId) throw new Error('Cannot determine client application ID');
      const csp = await resolveServicePrincipalId(tokenG, clientAppId);
      setClientSpId(csp);

      // Determine resource identifier
      let resourceIdentifier = trimmedResourceMemo;
      if (!resourceIdentifier) {
        resourceIdentifier = isGraphResourceMemo ? GRAPH_APPID : '';
      }
      if (!resourceIdentifier) throw new Error('Cannot determine resource. Set Resource (Application ID URI) or use Graph scopes.');
      const rsp = await resolveServicePrincipalId(tokenG, resourceIdentifier);
      setResourceSpId(rsp);

      // Load grants
      const url = `${GRAPH}/v1.0/oauth2PermissionGrants?$filter=clientId eq '${csp}' and resourceId eq '${rsp}'`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tokenG}` } });
      if (!res.ok) throw new Error(`Graph grants query failed (${res.status})`);
      const data = await res.json();
      setGrants(Array.isArray(data?.value) ? data.value : []);
      if (!Array.isArray(data?.value) || data.value.length === 0) {
        setConsentError('No delegated consent grants found for this client → resource pair.');
      }
    } catch (e: any) {
      setConsentError(e?.message || String(e));
    } finally {
      setConsentLoading(false);
    }
  };

  const sendTestGet = async () => {
    if (!token || !testUrl.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const start = performance.now();
      const res = await fetch(testUrl.trim(), { headers: { Authorization: `Bearer ${token}` } });
      const ms = Math.round(performance.now() - start);
      const wwwAuth = res.headers.get('www-authenticate') || res.headers.get('WWW-Authenticate') || undefined;
      setTestResult({ status: res.status, ms, wwwAuth });
    } catch (e: any) {
      setTestResult({ error: e?.message || String(e) });
    } finally {
      setTesting(false);
    }
  };

  // Scenario helper to pre-fill fields quickly
  type ScenarioConfig = {
    resource?: string;
    useDefault?: boolean;
    scopes?: string[];
    authority?: string;
    clientIdOverride?: string;
    forceConsent?: boolean;
    forceRefresh?: boolean;
    testUrl?: string;
  };
  const applyScenario = (cfg: ScenarioConfig) => {
    if (cfg.resource !== undefined) setResource(cfg.resource);
    if (cfg.useDefault !== undefined) setUseDefault(cfg.useDefault);
    if (cfg.scopes !== undefined) setScopes(cfg.scopes);
    if (cfg.authority !== undefined) { setAuthority(cfg.authority); setAuthorityError(''); }
    if (cfg.clientIdOverride !== undefined) setClientIdOverride(cfg.clientIdOverride);
    if (cfg.forceConsent !== undefined) setForceConsent(cfg.forceConsent);
    if (cfg.forceRefresh !== undefined) setForceRefresh(cfg.forceRefresh);
    if (cfg.testUrl !== undefined) setTestUrl(cfg.testUrl);
    // Clear previous results to prevent confusion
    setError(null);
    setToken(null);
    setTestResult(null);
    if (cfg.scopes || cfg.resource || cfg.useDefault || cfg.authority || cfg.clientIdOverride || cfg.testUrl) {
      openSnack('Scenario applied', 'info');
    }
  };

  // Reset configuration and scopes to starting settings
  const resetToDefaults = () => {
    setScopeInput('openid profile');
    setScopes(['openid', 'profile']);
    setResource('');
    setUseDefault(false);
    setAuthority('https://login.microsoftonline.com');
    setClientIdOverride('');
    setForceConsent(false);
    setForceRefresh(false);
    setSelectedPresetId(null);
    setAuthorityError('');
    // Also reset consent/roles helper state
    setConsentLoading(false);
    setConsentError(null);
    setClientSpId('');
    setResourceSpId('');
    setGrants([]);
    setRolesLoading(false);
    setRolesError(null);
    setRoleAssignments([]);
    // Clear transient results for clarity
    setError(null);
    setToken(null);
    setTestUrl('');
    setTestResult(null);
    setTestHint('');
    // Clear authorization request display
    setLastAuthUrl('');
    setAuthRequestExpanded(false);
    openSnack('Reset to defaults', 'info');
  };

  // Consent diagnostics helpers
  const adminConsentUrl = useMemo(() => {
    try {
      const tenant = account?.tenantId;
      const client = (decoded?.payload?.appid || decoded?.payload?.azp || clientIdOverride || '').toString();
      const resUri = (resource || (decoded?.payload?.aud || '')).toString();
      if (!tenant || !client || !resUri) return '';
      const base = `https://login.microsoftonline.com/${tenant}/v2.0/adminconsent`;
      const params = new URLSearchParams({ client_id: client, redirect_uri: window.location.origin, scope: `${resUri}/.default`, state: 'appconfig-consent' });
      return `${base}?${params.toString()}`;
    } catch {
      return '';
    }
  }, [account?.tenantId, decoded?.payload?.appid, decoded?.payload?.azp, decoded?.payload?.aud, clientIdOverride, resource]);

  // Requested vs granted delta
  const requestedScopeNames = useMemo(() => {
    if (useDefault) return [] as string[];
    return computedScopes.map((s) => {
      const idx = s.lastIndexOf('/');
      return idx >= 0 ? s.slice(idx + 1) : s;
    }).filter(Boolean);
  }, [computedScopes, useDefault]);
  const grantedScopes = useMemo(() => {
    const raw = decoded?.payload?.scp ? String(decoded.payload.scp) : '';
    return raw.split(/\s+/).filter(Boolean);
  }, [decoded?.payload?.scp]);
  const scopeDelta = useMemo(() => {
    const reqSet = new Set(requestedScopeNames);
    const grSet = new Set(grantedScopes);
    const requestedOnly = requestedScopeNames.filter((s) => !grSet.has(s));
    const extraGranted = grantedScopes.filter((s) => !reqSet.has(s));
    return { requestedOnly, extraGranted };
  }, [requestedScopeNames, grantedScopes]);

  const consentHints = useMemo(() => {
    const hints: string[] = [];
    if (error) {
      if (/AADSTS65001|consent/i.test(error)) hints.push('User or admin consent required for one or more scopes.');
      if (/invalid_scope/i.test(error)) hints.push('Invalid or unknown scope requested. Check spelling and resource.');
      if (/invalid_grant/i.test(error)) hints.push('Invalid grant. Try Force consent or Ignore cache; verify account context.');
    }
    if (testResult?.status === 401) hints.push('API returned 401 Unauthorized. Check scopes/roles and resource (audience).');
    if (testResult?.wwwAuth) hints.push('Review WWW-Authenticate header for required scopes or details.');
    return hints;
  }, [error, testResult?.status, testResult?.wwwAuth]);

  // Inline token preview state & derived JSON (collapsed by default)
  const [tokenPreviewOpen, setTokenPreviewOpen] = useState(false);
  const decodedHeaderJson = useMemo(() => decoded ? JSON.stringify(decoded.header, null, 2) : '', [decoded]);
  const decodedPayloadJson = useMemo(() => decoded ? JSON.stringify(decoded.payload, null, 2) : '', [decoded]);

  // Render
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* snackbars */}
      <Snackbar open={!!snack?.open} autoHideDuration={2500} onClose={closeSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={closeSnack} severity={snack?.severity || 'success'} variant="filled" sx={{ width: '100%' }}>
          {snack?.message}
        </Alert>
      </Snackbar>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f4f8fb', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ mr: 2, borderRadius: 2, pl: 1.5, color: 'text.secondary', borderColor: 'divider', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', borderColor: 'primary.main', color: 'primary.main' } }}
          >
            Back
          </Button>
          <Box sx={{ borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 0.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', letterSpacing: '-0.01em' }}>
              Token & Consent Tester
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Compose and request tokens for Microsoft Graph or custom APIs with consent and tenant controls
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Configuration & Scopes */}
      <Card variant="outlined" sx={{ 
        borderRadius: 2, 
        mb: 2,
        bgcolor: '#f8fafc', // Light blue-gray background
        border: '1px solid #e2e8f0',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }
      }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 1.5, 
            pb: 1, 
            borderBottom: '2px solid', 
            borderColor: 'primary.main',
            bgcolor: '#ffffff',
            mx: -2,
            mt: -2,
            px: 2,
            pt: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>Configuration</Typography>
            </Box>
            <Tooltip title="Reset to starting settings">
              <span>
                <Button size="small" color="inherit" onClick={resetToDefaults}>Reset</Button>
              </span>
            </Tooltip>
          </Box>
          <Stack spacing={2.5} sx={{ mb: 2.5, mt: 3 }}>
            {/* Authority first (dropdown) */}
            <Tooltip title="Select the tenant authority for token acquisition.">
              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontWeight: 500, color: 'text.secondary' }}>Authority (tenant)</InputLabel>
                  <Select
                    label="Authority (tenant)"
                    value={selectedTenant || (account?.tenantId || 'organizations')}
                    onChange={(e) => {
                      const t = String(e.target.value);
                      setAuthority(`https://login.microsoftonline.com/${t}`);
                      setAuthorityError('');
                    }}
                  >
                    {tenantOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Uses base https://login.microsoftonline.com with the selected tenant.
                </Typography>
              </Box>
            </Tooltip>

            {/* Resource second */}
            <Tooltip title="For custom APIs, specify the Application ID URI. Leave empty to use Graph-style shorthand scopes.">
              <Box>
                <TextField
                  label="Resource (Application ID URI)"
                  placeholder="api://<client-id> or https://contoso.com/api"
                  value={resource}
                  onChange={(e) => setResource(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fff',
                      borderRadius: 1.5,
                      '& fieldset': { borderColor: 'divider' },
                      '&:hover fieldset': { borderColor: 'primary.light' },
                    },
                    '& .MuiInputLabel-root': { fontWeight: 500, color: 'text.secondary' },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Clear">
                          <span>
                            <IconButton size="small" edge="end" onClick={() => setResource('')} disabled={!resource}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Tooltip>

            {/* Client last */}
            <Tooltip title="Temporarily request tokens as another app registration (clientId). Useful for testing.">
              <Box>
                <TextField
                  label="Client App (override clientId)"
                  placeholder="Optional clientId of the requesting application"
                  value={clientIdOverride}
                  onChange={(e) => setClientIdOverride(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fff',
                      borderRadius: 1.5,
                      '& fieldset': { borderColor: 'divider' },
                      '&:hover fieldset': { borderColor: 'primary.light' },
                    },
                    '& .MuiInputLabel-root': { fontWeight: 500, color: 'text.secondary' },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Clear">
                          <span>
                            <IconButton size="small" edge="end" onClick={() => setClientIdOverride('')} disabled={!clientIdOverride}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <TextField
              label="Scopes (space/comma separated)"
              value={scopeInput}
              onChange={(e) => setScopeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !useDefault) {
                  e.preventDefault();
                  addScope();
                }
              }}
              size="small"
              fullWidth
              disabled={useDefault}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: 1.5,
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: 'primary.light' },
                },
                '& .MuiInputLabel-root': { fontWeight: 500, color: 'text.secondary' },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Clear">
                      <span>
                        <IconButton size="small" edge="end" onClick={() => setScopeInput('')} disabled={!scopeInput}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Add scope(s)"><span><IconButton color="primary" onClick={addScope} disabled={!scopeInput.trim() || useDefault} sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}><AddIcon /></IconButton></span></Tooltip>
            <Tooltip title="Save as preset"><span><IconButton onClick={savePreset} disabled={scopes.length === 0} sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}><SaveIcon /></IconButton></span></Tooltip>
            <Tooltip title="Update selected preset"><span><IconButton onClick={updatePreset} disabled={!selectedPresetId} sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}><SaveAsIcon /></IconButton></span></Tooltip>
      {/* Update preset confirmation dialog */}
      <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)}>
        <DialogTitle>Update Preset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to update the selected preset with the current configuration and scopes?
            <br />
            <b>{selectedPresetId ? presets.find((p) => p.id === selectedPresetId)?.name : ''}</b>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleUpdatePresetConfirmed} color="primary" variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
          </Stack>
          <Box sx={{ mt: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {!useDefault && scopes.map((s) => (
              <Chip key={s} label={s} onDelete={() => removeScope(s)} color="default" size="small" variant="outlined" sx={{ borderRadius: 1 }} />
            ))}
            {useDefault && resource && (
              <Chip color="primary" label={`${resource.replace(/\/$/, '')}/.default`} size="small" sx={{ borderRadius: 1 }} />
            )}
          </Box>

          {presets.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Presets</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {presets.map((p) => (
                  <Chip
                    key={p.id}
                    label={p.name}
                    color={selectedPresetId === p.id ? 'primary' : 'default'}
                    onClick={() => applyPreset(p.id)}
                    onDelete={() => deletePreset(p.id)}
                    deleteIcon={<DeleteIcon />}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          {/* User selection */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">User:</Typography>
            <Chip size="small" label={activeAccount?.username || '(none)'} />
            <Button size="small" variant="outlined" onClick={switchUser}>Switch user…</Button>
            {selectedAccount && selectedAccount.username !== account?.username && (
              <Tooltip title="Sign out test user and revert to primary AppConfig account">
                <Button 
                  size="small" 
                  variant="outlined" 
                  color="warning"
                  onClick={signOutTestUser}
                >
                  Sign out test user
                </Button>
              </Tooltip>
            )}            
          </Stack>

          {/* Tip - only show when test user is active */}
          {selectedAccount && selectedAccount.username !== account?.username && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 2, fontStyle: 'italic' }}>
              💡 Tip: Use "Sign out test user" button to clear test accounts from cache before signing out of AppConfig
            </Typography>
          )}          

          <Stack direction="row" spacing={2} alignItems="center">
            <Tooltip title="Forces an interactive popup with prompt=consent so the user can grant any newly added scopes. Use this for incremental consent or to deliberately re-display the consent screen.">
              <Box>
                <FormControlLabel control={<Switch checked={forceConsent} onChange={(e) => setForceConsent(e.target.checked)} />} label="Force consent (popup)" />
              </Box>
            </Tooltip>
            <Tooltip title="Bypasses the MSAL token cache (forceRefresh=true) to fetch a fresh token from Entra ID. Use after admin consent, role/app role changes, or revoking consent to ensure updated claims and scope grants are reflected.">
              <Box>
                <FormControlLabel control={<Switch checked={forceRefresh} onChange={(e) => setForceRefresh(e.target.checked)} />} label="Ignore cache (force refresh)" />
              </Box>
            </Tooltip>
            <Tooltip title="Include login_hint with the current user’s UPN to streamline account selection in popups.">
              <Box>
                <FormControlLabel control={<Switch checked={useLoginHint} onChange={(e) => setUseLoginHint(e.target.checked)} />} label="Use login_hint" />
              </Box>
            </Tooltip>
            <Tooltip title="Request the app’s configured permissions for the Resource via /.default. Ignores individual scope entries.">
              <Box>
                <FormControlLabel control={<Switch checked={useDefault} onChange={(e) => setUseDefault(e.target.checked)} />} label="Use /.default" />
              </Box>
            </Tooltip>
          </Stack>

          {/* Request Token Button Row */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              onClick={handleRequest} 
              disabled={requesting} 
              startIcon={!requesting ? <PlayArrowIcon /> : undefined} 
              sx={{ borderRadius: 2, minWidth: 180 }}
            >
              {requesting ? (<><CircularProgress size={18} sx={{ mr: 1 }} /> Requesting…</>) : 'Request Token'}
            </Button>
          </Box>

          {/* Authorization Request Display */}
          {lastAuthUrl && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#f8f9fa' }}>
              <Accordion expanded={authRequestExpanded} onChange={() => setAuthRequestExpanded(!authRequestExpanded)} sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 'auto', '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Last Authorization Request (Auth Code with PKCE)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {parsedAuth && (
                    <Box>
                      {/* Endpoint */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Chip label="Endpoint" size="small" variant="outlined" sx={{ mr: 1 }} />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                          {parsedAuth.endpoint}
                        </Typography>
                      </Box>

                      {/* Parameters */}
                      <Typography variant="body2" sx={{ mt: 1, mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
                        Parameters
                      </Typography>
                      <Stack spacing={0.75}>
                        {parsedAuth.params.map(([key, value]) => (
                          <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Chip 
                              label={key} 
                              size="small" 
                              variant="outlined" 
                              sx={{ minWidth: 150, justifyContent: 'flex-start', fontFamily: 'monospace' }} 
                            />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace', 
                                fontSize: '0.875rem', 
                                wordBreak: 'break-all', 
                                flex: 1,
                                color: key === 'code_challenge' || key === 'state' || key === 'nonce' ? 'text.secondary' : 'text.primary'
                              }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>

                      {/* Raw URL */}
                      <Box sx={{ mt: 2, pt: 1, borderTop: '1px dashed #e0e0e0' }}>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                          Raw URL
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              fontSize: '0.75rem', 
                              wordBreak: 'break-all', 
                              flex: 1, 
                              bgcolor: '#ffffff', 
                              p: 1, 
                              borderRadius: 1, 
                              border: '1px solid #e0e0e0' 
                            }}
                          >
                            {lastAuthUrl}
                          </Typography>
                          <Tooltip title="Copy URL">
                            <IconButton size="small" onClick={() => copy(lastAuthUrl)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Note about placeholders */}
                      <Alert severity="info" sx={{ mt: 2, borderRadius: 1 }}>
                        <Typography variant="caption">
                          <strong>Note:</strong> The <code>state</code>, <code>nonce</code>, and <code>code_challenge</code> values shown are placeholders. 
                          MSAL.js generates these dynamically for each request using cryptographically secure random values and PKCE (Proof Key for Code Exchange).
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Result */}
      <Card variant="outlined" sx={{ 
        borderRadius: 2,
        bgcolor: '#fefffe', // Very light green background
        border: '1px solid #e2e8f0',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }
      }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 1.5, 
            pb: 1, 
            borderBottom: '2px solid', 
            borderColor: 'success.main',
            bgcolor: '#ffffff',
            mx: -2,
            mt: -2,
            px: 2,
            pt: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>Response</Typography>
            </Box>
            <Button size="small" color="inherit" onClick={() => { setToken(null); setError(null); setTestUrl(''); setTestResult(null); }}>
              Clear
            </Button>
          </Box>
          <Box sx={{ mt: 1, p: 1.25, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: '#fafafa' }}>
            <Typography variant="caption" color="text.secondary">Computed scopes to request:</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {computedScopes.length === 0 ? (
                <Chip size="small" label="(none)" variant="outlined" />
              ) : (
                computedScopes.map((s) => <Chip key={s} size="small" label={s} />)
              )}
            </Box>
            {/* Copy scopes button removed as requested */}
            {multiResourceWarning && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{multiResourceWarning}</Typography>
            )}
          </Box>
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
          )}
          {!token && !error && (
            <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">No token yet. Enter scopes and click Request token.</Typography>
          )}
          {token && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                {decoded?.payload?.aud && <Chip size="small" label={`aud: ${decoded.payload.aud}`} />}
                {decoded?.payload?.scp && String(decoded.payload.scp).split(/\s+/).filter(Boolean).reduce((acc: string[], s: string) => (acc.includes(s) ? acc : [...acc, s]), []).map((s: string) => (
                  <Chip key={s} size="small" color="primary" label={s} />
                ))}
                {decoded?.payload?.roles && (Array.isArray(decoded.payload.roles) ? decoded.payload.roles : [decoded.payload.roles]).map((r: string) => (
                  <Chip key={r} size="small" color="secondary" label={r} />
                ))}
              </Stack>
              {/* Requested vs granted delta */}
              {!useDefault && (
                <Box sx={{ mt: 1.5, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: '#fafafa' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Requested vs granted</Typography>
                  <Typography variant="caption" color="text.secondary">Missing in token:</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {scopeDelta.requestedOnly.length === 0 ? (
                      <Chip size="small" label="(none)" variant="outlined" />
                    ) : (
                      scopeDelta.requestedOnly.map((s) => <Chip key={`reqOnly-${s}`} size="small" color="warning" label={s} />)
                    )}
                  </Box>
                  <Tooltip title="These come from persisted delegated consent (OAuth2PermissionGrants) for this client → resource. Entra ID returns the union of previously granted scopes and your current request. Revoke grants under Enterprise applications to remove, or use a separate client app for isolation.">
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', cursor: 'help' }}>Extra in token:</Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {scopeDelta.extraGranted.length === 0 ? (
                      <Chip size="small" label="(none)" variant="outlined" />
                    ) : (
                      scopeDelta.extraGranted.map((s) => <Chip key={`extra-${s}`} size="small" color="info" label={s} />)
                    )}
                  </Box>
                </Box>
              )}
              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1 }}>
                <Info label="Tenant" value={String(claim('tid') || '')} />
                <Info label="User" value={String(activeAccount?.username || '')} />
                <Info label="Resource (aud)" value={String(claim('aud') || '')} />
                <Info label="Client App ID" value={String(claim('appid') || claim('azp') || '')} />
                <Info label="Issued" value={claimsTime(claim('iat'))} />
                <Info label="Expires" value={claimsTime(claim('exp'))} />
              </Box>
              {/* <Box sx={{ mt: 1 }}>
                <Tooltip title="Copy decoded claims (header+payload) as JSON">
                  <span>
                    <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copy(JSON.stringify(decoded, null, 2))}>
                      Copy claims JSON
                    </Button>
                  </span>
                </Tooltip>
              </Box> */}
              {/* Inline decoded token preview */}
              {decoded && (
                <>
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={tokenPreviewOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      onClick={() => setTokenPreviewOpen(o => !o)}
                    >
                      {tokenPreviewOpen ? 'Hide decoded token' : 'Show decoded token'}
                    </Button>
                    {tokenPreviewOpen && (
                      <>
                        <Button size="small" onClick={() => copy(decodedHeaderJson)} disabled={!decodedHeaderJson}>Copy header</Button>
                        <Button size="small" onClick={() => copy(decodedPayloadJson)} disabled={!decodedPayloadJson}>Copy payload</Button>
                        <Tooltip title="Copy entire raw token string">
                          <span>
                            <Button size="small" onClick={() => copy(token!)} disabled={!token}>Copy raw token</Button>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                  <Collapse in={tokenPreviewOpen} timeout="auto" unmountOnExit>
                    <Box
                      sx={{ mt: 1, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fafafa' }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Decoded header & payload (preview). For signature validation & richer claim explanations open the full Token Decoder tool.
                      </Typography>
                      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', borderRadius: 1, bgcolor: '#fff', p: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Header</Typography>
                          <Box component="pre" sx={{ m: 0, p: 0, fontSize: 12, lineHeight: 1.4, maxHeight: 220, overflow: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace', width: '100%', boxSizing: 'border-box', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {decodedHeaderJson || '// (empty)'}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', borderRadius: 1, bgcolor: '#fff', p: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Payload</Typography>
                          <Box component="pre" sx={{ m: 0, p: 0, fontSize: 12, lineHeight: 1.4, maxHeight: 220, overflow: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace', width: '100%', boxSizing: 'border-box', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {decodedPayloadJson || '// (empty)'}
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        <Tooltip title="First part of the raw token (truncated). Use Copy raw token for full string.">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace', wordBreak: 'break-all' }}
                          >
                            {token ? `${token.slice(0, 90)}…` : '(no token)'}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Collapse>
                </>
              )}

              {/* Minimal test request */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Test request (GET)</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Autocomplete
                  options={graphGetPresets}
                  getOptionLabel={(o) => o.label}
                  onChange={(_e, v) => {
                    if (v) {
                      setTestUrl(v.url);
                      setTestHint(v.hint || '');
                    } else {
                      setTestHint('');
                    }
                  }}
                  renderInput={(params) => <TextField {...params} size="small" label="Common Graph endpoints" placeholder="Select…" />}
                  sx={{ width: { xs: '100%', sm: 320 } }}
                />
                <TextField
                  label="URL"
                  placeholder="https://graph.microsoft.com/v1.0/me"
                  size="small"
                  value={testUrl}
                  onChange={(e) => setTestUrl(e.target.value)}
                  fullWidth
                />
                <Button variant="outlined" onClick={sendTestGet} disabled={!testUrl.trim() || testing}>
                  {testing ? 'Sending…' : 'Send GET'}
                </Button>
              </Stack>
              {testHint && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Suggested scope(s): {testHint}
                </Typography>
              )}
              {testResult && (
                <Box sx={{ mt: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                  {typeof testResult.status === 'number' && (
                    <Typography variant="body2">
                      Status: <b>{testResult.status}</b>{typeof testResult.ms === 'number' ? ` • ${testResult.ms} ms` : ''}
                    </Typography>
                  )}
                  {testResult.wwwAuth && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      WWW-Authenticate: {testResult.wwwAuth}
                    </Typography>
                  )}
                  {testResult.error && (
                    <Typography variant="body2" color="error">{testResult.error}</Typography>
                  )}
                </Box>
              )}

              {/* Consent diagnostics */}
              {(error || (testResult && (testResult.status === 401 || testResult.wwwAuth))) && (
                <Box sx={{ mt: 2, p: 1, border: '1px dashed #cbd5e1', borderRadius: 1, bgcolor: '#fafafa' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Consent diagnostics</Typography>
                  {consentHints.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No specific hints available.</Typography>
                  ) : (
                    <ul style={{ marginTop: 0 }}>
                      {consentHints.map((h, i) => (
                        <li key={i}><Typography variant="body2">{h}</Typography></li>
                      ))}
                    </ul>
                  )}
                  {adminConsentUrl && (
                    <Button size="small" sx={{ mt: 0.5 }} onClick={() => window.open(adminConsentUrl, '_blank')}>Open admin consent</Button>
                  )}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* App-role assignments panel (moved directly under Result) */}
      <Card 
        variant="outlined" 
        sx={{ 
          borderRadius: 2, 
          mt: 3,
          background: 'linear-gradient(to right, #fafafa 0%, #ffffff 100%)',
          border: '1px solid #e0e0e0',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderColor: '#9c27b0'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                boxShadow: '0 2px 8px rgba(156, 39, 176, 0.25)'
              }}>
                <AssignmentIndIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#333', lineHeight: 1.2 }}>
                  App Role Assignments
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Application permissions assigned to current user
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                size="small" 
                color="inherit" 
                onClick={() => { setRolesLoading(false); setRolesError(null); setRoleAssignments([]); }}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
              >
                Clear
              </Button>
              <Button 
                size="small" 
                variant="contained"
                onClick={async () => {
              setRolesLoading(true);
              setRolesError(null);
              setRoleAssignments([]);
              try {
                const tokenG = await getGraphToken();
                // Ensure resource SP id
                let rsp = resourceSpId;
                if (!rsp) {
                  let resourceIdentifier = trimmedResourceMemo;
                  if (!resourceIdentifier) resourceIdentifier = isGraphResourceMemo ? GRAPH_APPID : '';
                  if (!resourceIdentifier) throw new Error('Cannot determine resource. Set Resource or use Graph scopes.');
                  rsp = await resolveServicePrincipalId(tokenG, resourceIdentifier);
                  setResourceSpId(rsp);
                }
                const headers = { Authorization: `Bearer ${tokenG}` } as any;
                // Some tenants/APIs return 400 with certain $filter on this endpoint; fetch and filter client-side
                const url = `${GRAPH}/v1.0/me/appRoleAssignments`;
                const res = await fetch(url, { headers });
                if (!res.ok) throw new Error(`Graph appRoleAssignments query failed (${res.status})`);
                const data = await res.json();
                const rawItems: Array<{ id: string; appRoleId: string; resourceId?: string }> = Array.isArray(data?.value) ? data.value : [];
                const items = rawItems.filter((it) => String(it.resourceId || '').toLowerCase() === String(rsp).toLowerCase()).map((it) => ({ id: it.id, appRoleId: it.appRoleId }));
                // Map role IDs to values by loading resource SP appRoles
                const spRes = await fetch(`${GRAPH}/v1.0/servicePrincipals/${rsp}?$select=appRoles`, { headers });
                if (!spRes.ok) throw new Error(`Graph servicePrincipal read failed (${spRes.status})`);
                const spData = await spRes.json();
                const roleMap = new Map<string, { value?: string; displayName?: string }>();
                if (Array.isArray(spData?.appRoles)) {
                  for (const r of spData.appRoles) {
                    if (r?.id) roleMap.set(String(r.id), { value: r?.value, displayName: r?.displayName });
                  }
                }
                const mapped = items.map((it) => ({
                  id: it.id,
                  appRoleId: it.appRoleId,
                  appRoleValue: roleMap.get(String(it.appRoleId))?.value,
                  appRoleDisplayName: roleMap.get(String(it.appRoleId))?.displayName,
                }));
                setRoleAssignments(mapped);
                if (mapped.length === 0) setRolesError('No app-role assignments found for this user on the selected resource.');
              } catch (e: any) {
                setRolesError(e?.message || String(e));
              } finally {
                setRolesLoading(false);
              }
            }} 
            disabled={rolesLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              boxShadow: '0 2px 6px rgba(156, 39, 176, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)',
                boxShadow: '0 4px 12px rgba(156, 39, 176, 0.4)'
              }
            }}>
                {rolesLoading ? 'Loading…' : 'Fetch Assignments'}
              </Button>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
              Shows <strong>application roles</strong> (app permissions) assigned to the signed-in user for the target resource. 
              These roles can appear as <code style={{ background: '#e3f2fd', padding: '2px 6px', borderRadius: 3, fontSize: '0.85em' }}>roles</code> claims in delegated access tokens.
            </Typography>
          </Alert>

          {rolesError && (
            <Alert severity="warning" sx={{ borderRadius: 1.5, mb: 2 }}>
              <Typography variant="body2">{rolesError}</Typography>
            </Alert>
          )}
          
          {roleAssignments.length > 0 && (
            <Paper 
              elevation={0}
              sx={{ 
                p: 2.5, 
                border: '2px solid #e8f5e9',
                borderRadius: 2,
                bgcolor: '#f1f8f4'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.3rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  {roleAssignments.length} Role Assignment{roleAssignments.length !== 1 ? 's' : ''} Found
                </Typography>
              </Box>
              
              <Stack spacing={1.5}>
                {roleAssignments.map((ra) => (
                  <Paper
                    key={ra.id}
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px solid #c8e6c9',
                      borderRadius: 1.5,
                      bgcolor: 'white',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#81c784',
                        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.15)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <AssignmentTurnedInIcon sx={{ color: '#66bb6a', fontSize: '1.4rem', mt: 0.3 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                          {ra.appRoleDisplayName || ra.appRoleValue || 'Unnamed Role'}
                        </Typography>
                        {(ra.appRoleDisplayName && ra.appRoleValue) && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            Value: {ra.appRoleValue}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                          ID: {ra.appRoleId}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
              
              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2, borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                  <strong>Note:</strong> Group-based role assignments may not appear in this list. 
                  Check the <code style={{ background: '#e3f2fd', padding: '2px 4px', borderRadius: 3 }}>roles</code> claim in your access token for the complete set of effective roles.
                </Typography>
              </Alert>
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Delegated Consent Grants (OAuth2PermissionGrants) */}
      <Card 
        variant="outlined" 
        sx={{ 
          borderRadius: 2, 
          mt: 3,
          background: 'linear-gradient(to right, #fafafa 0%, #ffffff 100%)',
          border: '1px solid #e0e0e0',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderColor: '#1976d2'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)'
              }}>
                <GavelIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#333', lineHeight: 1.2 }}>
                  Delegated Consent Grants
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  OAuth2PermissionGrants for client → resource
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                size="small" 
                color="inherit" 
                onClick={() => { setConsentLoading(false); setConsentError(null); setClientSpId(''); setResourceSpId(''); setGrants([]); }}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
              >
                Clear
              </Button>
              <Button 
                size="small" 
                variant="contained"
                onClick={fetchConsentStatus} 
                disabled={consentLoading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 2px 6px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)'
                  }
                }}
              >
                {consentLoading ? 'Loading…' : 'Check Consent'}
              </Button>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
              Checks existing <strong>delegated permission grants</strong> (OAuth2PermissionGrants) between the client app and target resource. 
              Shows what <code style={{ background: '#e3f2fd', padding: '2px 6px', borderRadius: 3, fontSize: '0.85em' }}>scp</code> scopes have been consented by users or admins.
            </Typography>
          </Alert>

          {/* Connection Info */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', borderRadius: 1.5, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#555', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LinkIcon fontSize="small" />
              Connection Details
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1.5 }}>
              <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e8e8e8' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>Client App ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                  {(decoded?.payload?.appid || decoded?.payload?.azp || clientIdOverride || (msalInstance as any)?.config?.auth?.clientId || '').toString() || '-'}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e8e8e8' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>Client SP Object ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all', color: clientSpId ? 'inherit' : 'text.disabled' }}>
                  {clientSpId || '(pending)'}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e8e8e8' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>Resource</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                  {trimmedResourceMemo || (isGraphResourceMemo ? 'Microsoft Graph' : '-')}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e8e8e8' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>Resource SP Object ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all', color: resourceSpId ? 'inherit' : 'text.disabled' }}>
                  {resourceSpId || '(pending)'}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {consentError && (
            <Alert severity="warning" sx={{ borderRadius: 1.5, mb: 2 }}>
              <Typography variant="body2">{consentError}</Typography>
            </Alert>
          )}
          
          {grants.length > 0 && (
            <Paper 
              elevation={0}
              sx={{ 
                p: 2.5, 
                border: '2px solid #e3f2fd',
                borderRadius: 2,
                bgcolor: '#f0f7ff'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircleIcon sx={{ color: '#1976d2', fontSize: '1.3rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0d47a1' }}>
                  {grantsGrouped.length > 0 ? `${grantsGrouped.length} Delegated Scope${grantsGrouped.length !== 1 ? 's' : ''} Granted` : 'Grants Found'}
                </Typography>
              </Box>
              
              {grantsGrouped.length > 0 ? (
                <Stack spacing={1.5}>
                  {grantsGrouped.map((g, idx) => (
                    <Paper
                      key={`grp-${idx}`}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid #bbdefb',
                        borderRadius: 1.5,
                        bgcolor: 'white',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#64b5f6',
                          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <SecurityIcon sx={{ color: '#42a5f5', fontSize: '1.4rem', mt: 0.3 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#0d47a1', mb: 0.5, fontFamily: 'monospace' }}>
                            {g.scope}
                          </Typography>
                          {g.consentTypes.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                              {g.consentTypes.map((ct, ctIdx) => (
                                <Chip 
                                  key={ctIdx}
                                  label={ct}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    bgcolor: ct === 'AllPrincipals' ? '#e8f5e9' : '#fff3e0',
                                    color: ct === 'AllPrincipals' ? '#2e7d32' : '#e65100',
                                    border: ct === 'AllPrincipals' ? '1px solid #a5d6a7' : '1px solid #ffcc80'
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No scopes with content found in grants
                </Typography>
              )}
              
              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2, borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                  <strong>Note:</strong> These are overall client → resource grants (may include admin consent or multiple users). 
                  For detailed <strong>per-user grants</strong> and to <strong>revoke or modify consent</strong>, use AppConfig's <strong>OAuth2 Consent Manager</strong> tool.
                </Typography>
              </Alert>
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Scenarios & Tips */}
      <Accordion 
        variant="outlined" 
        sx={{ borderRadius: 2, mt: 3, '&:before': { display: 'none' }, border: '1px solid #e2e8f0' }}
        defaultExpanded={false}
        disableGutters
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TipsAndUpdatesIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Scenarios & Tips</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>1) Graph delegated with incremental consent</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ul style={{ marginTop: 0 }}>
                <li><Typography variant="body2">Leave <b>Resource</b> empty (or set <b>https://graph.microsoft.com</b>).</Typography></li>
                <li><Typography variant="body2">Enter scopes (e.g., <b>User.Read</b> or <b>Mail.Read</b>).</Typography></li>
                <li><Typography variant="body2">Turn on <b>Force consent (popup)</b> to add missing scopes. Optionally enable <b>Use login_hint</b> to streamline popup selection.</Typography></li>
                <li><Typography variant="body2">Use the <b>Authority</b> dropdown to select a tenant; <b>common</b> or <b>organizations</b> works for Graph.</Typography></li>
                <li><Typography variant="body2">Request token and verify <b>scp</b> claims.</Typography></li>
                <li><Typography variant="body2">Use <b>Test request (GET)</b> to call Graph (e.g., <b>/me</b>).</Typography></li>
              </ul>
              <Button size="small" variant="outlined" onClick={() => applyScenario({ resource: '', useDefault: false, scopes: ['User.Read'], forceConsent: true, testUrl: 'https://graph.microsoft.com/v1.0/me' })}>
                Try this
              </Button>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>2) Custom API via /.default</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ul style={{ marginTop: 0 }}>
                <li><Typography variant="body2">Set <b>Resource</b> to your Application ID URI (e.g., <b>api://&lt;client-id&gt;</b>).</Typography></li>
                <li><Typography variant="body2">Toggle <b>Use /.default</b> to request the app’s configured permissions.</Typography></li>
                <li><Typography variant="body2">If single-tenant, select your tenant from the <b>Authority</b> dropdown.</Typography></li>
                <li><Typography variant="body2">Request token, then run a <b>Test request (GET)</b> to your API (e.g., <b>/health</b>).</Typography></li>
              </ul>
              <Button size="small" variant="outlined" onClick={() => applyScenario({ resource: 'api://<your-client-id>', useDefault: true, authority: account?.tenantId ? `https://login.microsoftonline.com/${account.tenantId}` : authority, testUrl: 'https://your-api.example.com/health' })}>
                Try this
              </Button>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>3) Isolate scope sets with Client App override</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ul style={{ marginTop: 0 }}>
                <li><Typography variant="body2">Enter a <b>Client App (override clientId)</b> for a test registration with minimal permissions.</Typography></li>
                <li><Typography variant="body2">Turn on <b>Force consent</b> for first-time use.</Typography></li>
                <li><Typography variant="body2">Request token and confirm its <b>scp</b> matches only that test client’s grant. Use <b>Consent status</b> to review persisted (sticky) grants.</Typography></li>
                <li><Typography variant="body2">Save a Preset for future reuse.</Typography></li>
              </ul>
              <Button size="small" variant="outlined" onClick={() => applyScenario({ resource: '', useDefault: false, scopes: ['User.Read'], clientIdOverride: '', forceConsent: true, testUrl: 'https://graph.microsoft.com/v1.0/me' })}>
                Try this
              </Button>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>4) Tenant-specific authority & multi-tenant tips</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ul style={{ marginTop: 0 }}>
                <li><Typography variant="body2">Use the <b>Authority</b> dropdown to pick a tenant (<b>common</b>/<b>organizations</b> for multi-tenant or your <b>tenantId</b> for single-tenant).</Typography></li>
                <li><Typography variant="body2">Scopes must belong to a single resource; mixed resources will be rejected.</Typography></li>
              </ul>
              <Button size="small" variant="outlined" onClick={() => applyScenario({ authority: account?.tenantId ? `https://login.microsoftonline.com/${account.tenantId}` : authority })}>
                Try this
              </Button>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>5) Diagnose 401 Unauthorized</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ul style={{ marginTop: 0 }}>
                <li><Typography variant="body2">Use <b>Test request (GET)</b> and read <b>WWW-Authenticate</b> for required scopes.</Typography></li>
                <li><Typography variant="body2">Try <b>Force consent</b>, verify you’re targeting the correct <b>Resource (aud)</b>, and confirm <b>Requested vs granted</b> shows the needed scopes.</Typography></li>
                <li><Typography variant="body2">Use <b>Open admin consent</b> if user consent is blocked.</Typography></li>
              </ul>
              <Button size="small" variant="outlined" onClick={() => applyScenario({ forceConsent: true })}>
                Try this
              </Button>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>6) App-only (application) tokens — note</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 1 }}>
                App-only tokens are obtained by confidential clients (secret/cert) using <b>/.default</b> for admin-consented app roles.
              </Typography>
              <ul style={{ marginTop: 0 }}>
                <li><Typography variant="body2">Use a backend (e.g., your API/Functions) to request app-only tokens.</Typography></li>
                <li><Typography variant="body2">Ensure admin consent is granted for the app roles you need.</Typography></li>
              </ul>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>7) Understand "Extra in token" scopes</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ul style={{ marginTop: 0 }}>
                <li><Typography variant="body2">Request a token and open <b>Requested vs granted</b>. Hover over <b>Extra in token</b> for context.</Typography></li>
                <li><Typography variant="body2">Open <b>Consent status</b> and click <b>Fetch consent status</b> to view grouped <b>Grants</b> (persisted delegated consent).</Typography></li>
                <li><Typography variant="body2">Revoke in <b>Entra ID → Enterprise applications → [Your SPA] → Permissions</b> to remove sticky scopes, or use a separate client app.</Typography></li>
              </ul>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>8) Why roles appear in the token</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ul style={{ marginTop: 0 }}>
                <li><Typography variant="body2">After requesting a token, check the <b>roles</b> chips in <b>Result</b>.</Typography></li>
                <li><Typography variant="body2">Open <b>App‑role assignments</b> and click <b>Fetch assignments</b> to see user → resource role assignments and names.</Typography></li>
                <li><Typography variant="body2">Note: Group-based role assignments may not list directly; rely on the token’s roles to see effective assignments.</Typography></li>
              </ul>
            </AccordionDetails>
          </Accordion>
        </AccordionDetails>
      </Accordion>

      {/* About */}
      <Accordion 
        variant="outlined" 
        sx={{ 
          borderRadius: 2, 
          bgcolor: '#f9f9f9', 
          mt: 2,
          '&:before': { display: 'none' }, // Remove default MUI accordion divider
          border: '1px solid #e2e8f0'
        }}
        disableGutters
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            '&.Mui-expanded': {
              borderBottom: '2px solid',
              borderColor: 'info.main'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>About Token & Consent Tester</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            A comprehensive tool for requesting and testing access tokens with Microsoft Graph and custom APIs.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
              What you can do:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Request Graph tokens:</strong> Use shorthand scopes like "User.Read" or "Mail.Send" for Microsoft Graph API calls
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Test custom APIs:</strong> Set your Application ID URI and request tokens with specific scopes or /.default
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Control consent flow:</strong> Force interactive consent or bypass cache to test different permission scenarios
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Multi-tenant testing:</strong> Use the <em>Authority</em> dropdown or override client app to test cross-tenant scenarios
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Debug permissions:</strong> Decode token claims, compare <em>Requested vs granted</em>, inspect <em>Consent status</em>, and test API calls
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Switch user:</strong> Request tokens as a different user via popup and validate account-specific results
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Understand roles:</strong> View user <em>App‑role assignments</em> that can appear as roles in tokens
                </Typography>
              </li>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
              Common use cases:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.8 } }}>
              <li>
                <Typography variant="body2">
                  Testing incremental consent by adding new scopes to existing Graph permissions
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Validating custom API authentication before deploying to production
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Troubleshooting 401 Unauthorized errors with <em>Requested vs granted</em>, <em>Consent diagnostics</em>, and detailed token inspection
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Creating reusable presets for different environments or permission sets
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Explaining extra scopes via grouped <em>Grants</em> in <em>Consent status</em>
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Explaining roles claim via <em>App‑role assignments</em>
                </Typography>
              </li>
            </Box>
          </Box>

          {/* Comparison with AuthenticateAs */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
              Token & Consent Tester vs. Test Authentication (AppTester)
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1.25 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Token & Consent Tester (this tool):</strong> Tests authentication <em>with your current app</em> (the one you're using now). 
                  While you can use the "Client App (override clientId)" field to temporarily test as a different app, this tool is primarily designed for 
                  rapid iteration on scopes, authorities, and consent flows during active development sessions.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Test Authentication (AppTester → Test Authentication):</strong> Tests authentication <em>as a different app registration and different user</em> in a full testing context. 
                  This tool is integrated into the AppTester workflow, making it ideal when you need to:
                </Typography>
                <Box component="ul" sx={{ mt: 0.5, pl: 2, '& li': { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body2">
                      Verify token claims after making changes to <strong>app roles</strong>, <strong>optional claims</strong>, or <strong>claims mapping policies</strong>
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Test how end users will experience authentication with your app registration from a fresh session
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Validate audience (aud) claims and verify cross-app token flows between services
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Confirm that configuration changes (like exposed scopes or API permissions) are correctly reflected in tokens
                    </Typography>
                  </li>
                </Box>
              </li>
            </Box>
            <Typography variant="body2" sx={{ mt: 1.5, fontStyle: 'italic', color: 'text.secondary' }}>
              💡 <strong>Quick guide:</strong> Use <em>Token & Consent Tester</em> for rapid scope/permission testing during development iterations. 
              Use <em>Test Authentication (AppTester)</em> when you've modified app configuration (roles, claims, permissions) and need to verify 
              those changes appear correctly in tokens issued for that specific app registration.
            </Typography>
          </Box>          

          <Box sx={{ p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7', mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              💡 Pro tip:
            </Typography>
            <Typography variant="body2">
              Use the "Scenarios & Tips" section above to quickly set up common authentication patterns. 
              Each scenario pre-fills the configuration for you.
            </Typography>
          </Box>

          <Box sx={{ p: 2, bgcolor: '#e7f3ff', borderRadius: 1, border: '1px solid #b3d9ff' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              🔒 Security note:
            </Typography>
            <Typography variant="body2">
              Tokens are handled client-side and never stored persistently. Only configuration presets are saved to browser storage. 
              Always review requested scopes and ensure they follow the principle of least privilege.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
}

const Info: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
  </Box>
);

function claimsTime(epoch?: number): string {
  if (!epoch) return '';
  const d = new Date(epoch * 1000);
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  const rel = mins >= 0 ? `in ${mins}m` : `${Math.abs(mins)}m ago`;
  return `${d.toLocaleString()} (${rel})`;
}

export default TokenScopeRequester;
